// 批次 23 Wave 3（#25，Gitea issue #25）：表單精靈／常用題型格式維護 complexBody×2。
//
// ── FormTemplateBody（form-template-form-template 表單精靈，origin formTemplate/formTemplate.jsp
//    727 行）──────────────────────────────────────────────────────────────────────
// 兩態（ready :52-64）：URL ?formTemplateId=0（或無參）＝新增表單（addTemplate UI：版本表/
// 版本匯入/新增版本鈕隱藏、purpose 可編、存檔直 doSaveFormTemplate :234-236）；否則＝編輯既有
// （版本列表＋版本匯入＋purpose disabled :64、存檔先 sysConfirm『是否確定要修改表單?』:238）。
// 鏡像範圍（誠實殼）：
//   - 表頭表單（契約 15 欄：3 hidden＋12 可見）：name/purpose/formTypeId/sort/description 可編；
//     10 個非鏡像欄（organizationId/jobTypeId/departmentId/formTag/invisibleRole/hideDataRole/
//     verifyerId/formTemplateId/formTemplateVersionId/csrfToken）以 hidden input 帶 name
//     （C5-h 語意；legacy selectpicker/chosen 群不鏡像＝D23-17）。
//   - 存檔 saveBtn → saveFormTemplate（:234-244）→ doSaveFormTemplate（:246-325 FormData）→
//     dataSource.update（寫入目標 FOR-114-0001＝mock 真列 id 權宜，F14；endpoint 未映射＝D21-4 同款）
//     ＋info『存檔成功【雛形值待實機校準】』。
//   - 回表單庫管理 → 站內導航 /formTemplate/formTemplateList（:630 一帶）。
//   - 版本列表（formTable :707；欄 :74-124）：操作1（編輯/預覽/匯出/刪除[verify!='PASS']）＋
//     操作2（複製）＋version/annualYear/startDateString/flowSummary/verify（:122-140 四態字色＋
//     送出審核[verify 空]/通過/未通過[verify=='NOT'＋verifyerId==loginAccountId→D23-9 種子旗標]）。
//     編輯/預覽＝提示呈現（B1-3 先例：**表單精靈設計器雛形未鏡像**，D23-13）；匯出＝Blob；
//     刪除＝confirm（doDelete :391-406）＋remove；複製＝sysConfirm『是否確定要複製為新版本?』
//     （doClone :413-431）＋create 新版列。sendVerify（:361-371 target='FormTemplateVersion'）→
//     update＋re-query＋info(desc)。
//   - 新增表單版本（addVersionBtn :666 setFormVersion(0)）＝create 新版列＋提示（設計器不鏡像＝D23-13）。
//   - 版本匯入（importBtn :671 doImport :433-475 file）＝檔名輸入＋update 誠實標註（檔案不落地 B2-1）。
// 版本/表頭資料＝種子（FORM_TEMPLATE_VERSION_SEED）【雛形值待實機校準】。
//
// ── FieldTypeListBody（form-template-field-type-list 常用題型格式維護，origin
//    formTemplate/fieldTypeList.jsp 253 行）──────────────────────────────────────
// 查詢 3 select（organizationId/jobTypeId/**type 欄位類型**，fieldTypeForm :186-218）＋表 3 欄
// （操作：複製[showClone]/刪除[showDelete]；職類 jobTypeName；名稱(顯示於下拉選單) name＝
// showUpdate 連結，:45-67）＋名稱連結開題型編輯 modal（欄位簡化：name/sampleTitle(textarea，
// CKEDITOR 以文字域鏡像)/subjectStyle/format/grade【雛形值待實機校準】；137 契約欄＝
// formFieldsConfig.js 動態題型表單，餘欄不鏡像＝D23-14）＋存檔驗證『未輸入名稱!』
// (js-my/fieldTypeList.js:83-87)＋saveFieldTypeAjax（:145）→ dataSource.update（dataEffect update）
// ＋複製（create 新列『(複製)』）＋刪除（confirm＋remove）。
// 題型資料＝種子（FIELD_TYPE_SEED）【雛形值待實機校準】。
//
// 誠實標註：D23-13（設計器不鏡像）、D23-14（題型欄位簡化＋未鏡像條件：id>=10000 連結條件/
// jobTypeId==0 系統內建範例）、D23-17（表單精靈非鏡像欄 hidden 化）、D21-4（未映射 endpoint）、
// F14（mock 寫入目標真列 id 權宜）。行號 2026-09-28 親驗。
//
// 【證據等級】§7 第 3 級（原始碼）。

import { useRef, useState, type ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { panelHeadingLevelFor } from '@/app/panel-heading-levels';
import { LegacyAsideBlocks } from '@/mocks/legacy-aside-notes';
import { Button } from '@/components/base/Button';
import { Select } from '@/components/base/Select';
import { Table, type TableColumn } from '@/components/base/Table';
import { TextInput } from '@/components/base/TextInput';
import { ContentPanel, ModalFrame, ProcessingOverlay, useDialogs } from '@/components/layouts';
import { t } from '@/messages/zh-TW';
import { useDataSource } from '@/mocks';
import { selectOptionsOverride } from '@/mocks/legacy-fields';
import type { Row } from '@/mocks/types';
import {
  ACCOUNT_JOB_TYPE_OPTIONS,
  ACCOUNT_ORGANIZATION_OPTIONS,
  FIELD_TYPE_SEED,
  FORM_TEMPLATE_PURPOSE_BODY_OPTIONS,
  FORM_TEMPLATE_VERSION_SEED,
} from '@/mocks/resource-rules';
import { legacyAlertFallback, downloadTemplateBlob } from '../templates/page-behaviors';
import { toUiKey } from '../templates/shared';
import { FORM_TYPE_OPTIONS } from './FormTemplateListBody';

const FORM_TEMPLATE_LIST_PATH = '/formTemplate/formTemplateList';

// ---------------------------------------------------------------------------
// 表單精靈（form-template-form-template）
// ---------------------------------------------------------------------------

/** 審核字色（formTemplate.jsp:123-132：未審核=text-success／審核中+未通過=text-danger）。 */
function verifyToneOf(verify: string): string {
  if (verify === '') return 'text-[#1ab394]';
  if (verify === 'NOT' || verify === 'FAIL') return 'text-danger';
  return '';
}

export function FormTemplateBody({ pageId }: { pageId: string }): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  const navigate = useNavigate();
  const location = useLocation();

  // 批次 52 修訂（b52-calibrate3，F-2）：OPTION_SET_OVERRIDES 命中＝凍結選項集取代寫死常數（規格 §7.1）。
  const overrideOptions = (name: string) => selectOptionsOverride(pageId, name);

  // 兩態（ready :52-64）：?formTemplateId=0／無參＝新增；其他＝編輯既有。
  const urlTemplateId = new URLSearchParams(location.search).get('formTemplateId') ?? '0';
  const isEdit = urlTemplateId !== '0';

  // 表頭表單（契約欄位子集；【雛形值待實機校準】：legacy 由 formTemplateForm 帶入既有值）。
  const [name, setName] = useState('急救訓練檢核表');
  const [purpose, setPurpose] = useState('plan');
  const [formTypeId, setFormTypeId] = useState('1');
  const [sort, setSort] = useState('1');
  const [description, setDescription] = useState('');
  const [verifyerIsMe] = useState('1'); // D23-9 種子旗標（verifyerId==loginAccountId；種子常數，Session 無帳號 id）
  // 10 個非鏡像契約欄（selectpicker/chosen 群＝D23-17）：hidden input 帶 name，值以 **native
  // change listener** 記入 extraRef（React synthetic onChange 對 hidden input 不觸發；jsdom 的
  // dirty-flag 會在 re-render 時重置 uncontrolled 值）——存檔讀 ref＝legacy 整表單序列化語意。
  const extraRef = useRef<Record<string, string>>({
    formTemplateVersionId: '0',
    organizationId: '',
    jobTypeId: '',
    departmentId: '',
    formTag: '',
    invisibleRole: '',
    hideDataRole: '',
    verifyerId: '',
    csrfToken: '',
  });
  const EXTRA_HIDDEN_NAMES: readonly string[] = [
    'formTemplateVersionId',
    'organizationId',
    'jobTypeId',
    'departmentId',
    'formTag',
    'invisibleRole',
    'hideDataRole',
    'verifyerId',
    'csrfToken',
  ];
  const hiddenInputRef = (hiddenName: string) => (el: HTMLInputElement | null) => {
    if (el === null) return;
    el.addEventListener('change', () => {
      extraRef.current[hiddenName] = el.value;
    });
  };
  const [versions, setVersions] = useState<readonly Row[]>(FORM_TEMPLATE_VERSION_SEED);
  const [importFileName, setImportFileName] = useState('');
  const [busy, setBusy] = useState(false);

  async function doSave(): Promise<void> {
    if (isEdit) {
      // 編輯既有：sysConfirm『是否確定要修改表單?』（:237-243）；新增（formTemplateId==0）直存（:235-236）。
      const confirmed = await dialogs.confirm('是否確定要修改表單?');
      if (!confirmed) {
        return;
      }
    }
    setBusy(true);
    try {
      // doSaveFormTemplate（:246-325；endpoint 未映射 → update 誠實標註）。
      // legacy formTemplateForm 整表單序列化（含 uploadFile 版本匯入欄與 10 個非鏡像欄）——payload 全帶。
      // 存檔讀 native listener 記錄的驅動值（＝legacy 整表單序列化；formTemplateVersionId 預設 '0'）。
      await dataSource.update(pageId, 'FOR-114-0001', {
        name,
        purpose,
        formTypeId,
        sort,
        description,
        uploadFile: importFileName,
        formTemplateId: isEdit ? urlTemplateId : '0',
        ...extraRef.current,
      });
      setBusy(false); // 先收遮罩再跳資訊對話框（issue #56）
      await dialogs.info('存檔成功【雛形值待實機校準】');
    } catch (cause: unknown) {
      setBusy(false);
      await dialogs.alert(legacyAlertFallback(cause));
    } finally {
      setBusy(false);
    }
  }

  /** sendVerify（:361-371；target='FormTemplateVersion'；寫入目標＝F14 權宜真列 id）。 */
  async function sendVerify(row: Row, verify: string): Promise<void> {
    setBusy(true);
    try {
      await dataSource.update(pageId, 'FOR-114-0001', { verifyTarget: 'FormTemplateVersion', versionId: String(row['id'] ?? ''), verify });
      setVersions((current) => current.map((candidate) => (candidate === row ? { ...candidate, verify } : candidate)));
      setBusy(false); // 先收遮罩再跳資訊對話框（issue #56）
      await dialogs.info('審核完成【雛形值待實機校準】');
    } catch (cause: unknown) {
      setBusy(false);
      await dialogs.alert(legacyAlertFallback(cause));
    } finally {
      setBusy(false);
    }
  }

  /** 新增表單版本（setFormVersion(0) :666；設計器不鏡像＝D23-13，以建新版本列＋提示等價）。 */
  async function addVersion(): Promise<void> {
    const maxVersion = versions.reduce((max, row) => Math.max(max, Number(row['version'] ?? 0)), 0);
    setBusy(true);
    try {
      await dataSource.create(pageId, {
        formTemplateId: isEdit ? urlTemplateId : '0',
        version: String(maxVersion + 1),
        annualYear: '115',
        verify: '',
      });
      setVersions((current) => [
        ...current,
        {
          id: `ftv-new-${maxVersion + 1}`,
          version: String(maxVersion + 1),
          annualYear: '115',
          startDateString: '',
          flowSummary: '',
          verify: '',
        },
      ]);
      setBusy(false); // 先收遮罩再跳資訊對話框（issue #56）
      await dialogs.info('已新增表單版本（表單精靈設計器雛形未鏡像，D23-13）');
    } catch (cause: unknown) {
      setBusy(false);
      await dialogs.alert(legacyAlertFallback(cause));
    } finally {
      setBusy(false);
    }
  }

  /** 版本匯入（doImport :433-475；檔案不落地＝檔名輸入＋update 誠實標註）。 */
  async function doImport(): Promise<void> {
    if (importFileName === '') {
      await dialogs.alert('請選擇匯入檔案!');
      return;
    }
    setBusy(true);
    try {
      await dataSource.update(pageId, 'FOR-114-0001', { importFile: importFileName });
      setBusy(false); // 先收遮罩再跳資訊對話框（issue #56）
      await dialogs.info('版本匯入成功【雛形值待實機校準】');
      setImportFileName('');
    } catch (cause: unknown) {
      setBusy(false);
      await dialogs.alert(legacyAlertFallback(cause));
    } finally {
      setBusy(false);
    }
  }

  /** 刪除版本（doDelete :391-406；verify!='PASS' 才有鈕）。 */
  async function doDeleteVersion(row: Row): Promise<void> {
    const confirmed = await dialogs.confirm('是否確定要刪除?');
    if (!confirmed) {
      return;
    }
    setBusy(true);
    try {
      await dataSource.remove(pageId, String(row['id'] ?? ''));
      setVersions((current) => current.filter((candidate) => candidate !== row));
      setBusy(false); // 先收遮罩再跳資訊對話框（issue #56）
      await dialogs.info('刪除成功【雛形值待實機校準】');
    } catch (cause: unknown) {
      setBusy(false);
      await dialogs.alert(legacyAlertFallback(cause));
    } finally {
      setBusy(false);
    }
  }

  /** 複製版本（doClone :413-431；sysConfirm『是否確定要複製為新版本?』:415＋create 新版列）。 */
  async function doCloneVersion(row: Row): Promise<void> {
    const confirmed = await dialogs.confirm('是否確定要複製為新版本?');
    if (!confirmed) {
      return;
    }
    const maxVersion = versions.reduce((max, candidate) => Math.max(max, Number(candidate['version'] ?? 0)), 0);
    setBusy(true);
    try {
      await dataSource.create(pageId, { formTemplateId: isEdit ? urlTemplateId : '0', version: String(maxVersion + 1), verify: '' });
      setVersions((current) => [
        ...current,
        {
          ...row,
          id: `ftv-clone-${maxVersion + 1}`,
          version: String(maxVersion + 1),
          verify: '',
        },
      ]);
      setBusy(false); // 先收遮罩再跳資訊對話框（issue #56）
      await dialogs.info('複製成功【雛形值待實機校準】');
    } catch (cause: unknown) {
      setBusy(false);
      await dialogs.alert(legacyAlertFallback(cause));
    } finally {
      setBusy(false);
    }
  }

  const columns: readonly TableColumn[] = [
    { key: 'actions', header: '', align: 'center' },
    { key: 'clone', header: '', align: 'center' },
    { key: 'version', header: '版本', align: 'center' },
    { key: 'annualYear', header: '啟用學年度', align: 'center' },
    { key: 'startDateString', header: '啟用日期', align: 'center' },
    { key: 'flowSummary', header: '簽核流程', align: 'center' },
    { key: 'verify', header: '審核狀態', align: 'center' },
  ];

  return (
    <div data-ui-id={pageId}>
      <ContentPanel uiId={`${pageId}-panel`} title={isEdit ? '表單精靈' : '新增表單範本'} headingLevel={panelHeadingLevelFor(pageId)}>
        {/* 批次 43（#41）：legacy form[2]（action=/formTemplate/setFormFlow method=POST）＝表頭表單
            （42 節點：10 hidden＋labels/selects/buttons）；react 表頭表單包入 form（C5-h；
            native change listener 記值，存檔讀 ref）。 */}
        <form data-ui-id={`${pageId}-set-form-flow`} action="/formTemplate/setFormFlow" method="POST" onSubmit={(event) => event.preventDefault()}>
        {EXTRA_HIDDEN_NAMES.map((hiddenName) => (
          <input
            key={hiddenName}
            type="hidden"
            name={hiddenName}
            defaultValue={hiddenName === 'formTemplateVersionId' ? '0' : ''}
            ref={hiddenInputRef(hiddenName)}
          />
        ))}
        {/* 表頭表單（formTemplateForm；兩態 ready :52-64）。 */}
        <div data-ui-id={`${pageId}-header-form`} className="mb-4 grid grid-cols-2 gap-2">
          {(
            [
              ['name', '表單名稱：', name, setName],
              ['sort', '排序：', sort, setSort],
              ['description', '說明：', description, setDescription],
            ] as const
          ).map(([key, label, value, set]) => (
            <div key={key} className="flex items-center gap-2">
              <label className="w-28 shrink-0 text-sm" htmlFor={`${pageId}-header-${key}`}>
                {label}
              </label>
              <TextInput uiId={`${pageId}-header-${key}`} name={key} value={value} onChange={set} />
            </div>
          ))}
          <div className="flex items-center gap-2">
            <label className="w-28 shrink-0 text-sm" htmlFor={`${pageId}-header-purpose`}>
              用途：
            </label>
            {/* 編輯既有時 purpose disabled（:64）。 */}
            <Select
              uiId={`${pageId}-header-purpose`}
              name="purpose"
              value={purpose}
              onChange={setPurpose}
              options={overrideOptions('purpose') ?? FORM_TEMPLATE_PURPOSE_BODY_OPTIONS}
              disabled={isEdit}
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="w-28 shrink-0 text-sm" htmlFor={`${pageId}-header-form-type-id`}>
              表單類型：
            </label>
            <Select
              uiId={`${pageId}-header-form-type-id`}
              name="formTypeId"
              value={formTypeId}
              onChange={setFormTypeId}
              options={overrideOptions('formTypeId') ?? FORM_TYPE_OPTIONS}
            />
          </div>
          {/* 查詢全部版本（formTemplate.jsp:644-649 isQueryAllDiv；實機 raw DOM 為
              style=display:none 的 form-group＝normalize 後 @hidden div 群：label
              「查詢全部版本：」＋div>label>input[checkbox id=isQueryAll 無 name]＋&nbsp）。
              b49-triage 案 3 K1「缺 form[2]/div[24] 4 nodes」＝此群；比照 D23-17 非鏡像欄
              同族誠實殼但 DOM 形狀照實鏡像。checkbox 無 name＝observed 契約抽不到
              （legacy 同款，不入 form 序列化）＝非契約欄位，純結構鏡像。
              注意：:624-635「去識別期間」整段被 JSP 註解（<%-- 沒有用 --%>）＝實機不渲染、
              legacy raw DOM 無此欄（K1 的行號引用誤含註解段）——react 亦不畫（畫了反而
              多元素＋契約多欄）。 */}
          <div id="isQueryAllDiv" style={{ display: 'none' }} data-ui-id={`${pageId}-is-query-all-div`} className="flex items-center gap-2">
            <label className="w-28 shrink-0 text-sm">查詢全部版本：</label>
            <div>
              <label>
                <input id="isQueryAll" type="checkbox" data-ui-id={`${pageId}-is-query-all`} />
                &nbsp;
              </label>
            </div>
          </div>
          <div className="col-span-2 flex gap-2">
            {/* 回表單庫管理（:652 document.location.assign；站內導航鏡像）。 */}
            <Button uiId={`${pageId}-back-btn`} variant="default" size="sm" type="button" onClick={() => navigate(FORM_TEMPLATE_LIST_PATH)}>
              回表單庫管理
            </Button>
            {/* 存檔（saveBtn → saveFormTemplate :234-244；編輯先 confirm）。 */}
            <Button
              uiId={`${pageId}-save-btn`}
              variant="primary"
              size="sm"
              type="button"
              disabled={busy}
              dataEffect={`${isEdit ? 'update' : 'create'}:${pageId}`}
              onClick={() => {
                void doSave();
              }}
            >
              存檔
            </Button>
            {/* 新增表單版本（addVersionBtn :666 setFormVersion(0)；新增態隱藏 :58/:60）。 */}
            {isEdit ? (
              <Button uiId={`${pageId}-add-version-btn`} variant="default" size="sm" type="button" disabled={busy} onClick={() => void addVersion()}>
                新增表單版本
              </Button>
            ) : null}
          </div>
          {/* 版本匯入（importBtn :671 doImport；新增態隱藏 :62）。 */}
          {isEdit ? (
            <div className="col-span-2 flex items-center gap-2">
              <label className="text-sm" htmlFor={`${pageId}-import-file`}>
                版本匯入：
              </label>
              <TextInput uiId={`${pageId}-import-file`} name="uploadFile" value={importFileName} onChange={setImportFileName} placeholder=".xlsx 檔名（雛形文字輸入；檔案不落地）" />
              <Button
                uiId={`${pageId}-import-btn`}
                variant="default"
                size="sm"
                type="button"
                disabled={busy}
                dataEffect={`update:${pageId}`}
                onClick={() => {
                  void doImport();
                }}
              >
                版本匯入
              </Button>
            </div>
          ) : null}
        </div>
        </form>
        {/* 批次 43（#41）：legacy 右欄兩態（formTemplate.jsp:660-696）。編輯態＝addVersion
            （使用說明 ol＋說明 ul，最後 li text-danger）；新增態＝addTemplate（表單範本說明 ol）。
            非當前態以 @hidden 鏡像（legacy 兩區塊常駐 DOM、以 class 切換）。 */}
        <div
          data-ui-id={`${pageId}-add-version-note`}
          className={isEdit ? 'mb-4' : 'mb-4 hidden'}
        >
          <label className="mb-[5px] inline-block font-bold text-ink">新增表單版本：</label>
          {'使用說明:'}
          <ol>
            <li>表單精靈設計 ：從頭開始建置一張新的表單。</li>
            <li>現有版本匯入 ：僅限匯入bak檔，由其他表單範本匯出之檔案匯入到新的表單範本。</li>
          </ol>
          {'說明:'}
          <ul>
            <li>點選【編輯】按鈕進行『表單簽核流程』及『表單內容』的調整。</li>
            <li>當表單內容需調整時，可使用【複製】的功能。</li>
            <li>新版本審核通過後，系統會自動抓取最新版的表單版本發送。</li>
            <li>新建表單版本時需注意：使用不同版本的表單，會造成資料無法比對及匯出統計。</li>
            <li>當表單已經發出到學員身上後，不可再增刪流程及欄位，若要異動內容請【複製】新版。</li>
            <li className="text-danger">請盡量不要將表單刪除，刪除後會無法查詢被刪表單的設定。</li>
          </ul>
        </div>
        <div
          data-ui-id={`${pageId}-add-template-note`}
          className={isEdit ? 'hidden' : 'mb-4'}
        >
          {'表單範本說明:'}
          <ol>
            <li>一張【表單名稱】等於一個範本。</li>
            <li>【表單類型】系統會依據此分類，在計畫及課程下拉顯示表單。</li>
            <li>【360度表單標記】判斷360度表單。</li>
            <li>【隱藏表單】登入者符合設定的角色時，不會查到這張表單。</li>
            <li>【遮蔽表單流程】登入者符合設定的角色時，會把流程裡的人名遮蔽。</li>
            <li>【表單審核人員】選單的資料來源為具有「表單庫審核角色」的人員，並且是該職類的負責部門人員。</li>
          </ol>
        </div>
        {/* 版本列表（formTable :707；欄 :74-124；新增態不渲染＝批次 23 兩態凍結測試
            「version-table null」；legacy 新增態的 hidden formVersionDiv（div[6]）＝
            R-diff 缺 1 筆 AD 登記（兩態裁決系）。 */}
        {isEdit ? (
          <Table
            uiId={`${pageId}-version-table`}
            columns={columns}
            rows={versions}
            rowKey={(row) => String(row['id'] ?? '').toLowerCase()}
            emptyText={t('table.empty')}
            renderCell={(row, columnKey) => {
              const id = String(row['id'] ?? '').toLowerCase();
              const verify = String(row['verify'] ?? '');
              if (columnKey === 'actions') {
                return (
                  <span>
                    {/* 編輯/預覽＝提示呈現（B1-3 先例；設計器不鏡像＝D23-13）。 */}
                    <Button
                      uiId={`${pageId}-version-row-${id}-edit`}
                      variant="default"
                      size="sm"
                      type="button"
                      onClick={() => {
                        void dialogs.info('表單精靈設計器雛形未鏡像（D23-13）');
                      }}
                    >
                      編輯
                    </Button>{' '}
                    <Button
                      uiId={`${pageId}-version-row-${id}-preview`}
                      variant="default"
                      size="sm"
                      type="button"
                      onClick={() => {
                        void dialogs.info('表單精靈預覽雛形未鏡像（D23-13）');
                      }}
                    >
                      預覽
                    </Button>
                    <br />
                    <Button
                      uiId={`${pageId}-version-row-${id}-export`}
                      variant="default"
                      size="sm"
                      type="button"
                      onClick={() => {
                        downloadTemplateBlob(`formTemplate-${String(row['version'] ?? '')}.xlsx`);
                      }}
                    >
                      匯出
                    </Button>
                    {verify !== 'PASS' ? (
                      <>
                        {' '}
                        <Button
                          uiId={`${pageId}-version-row-${id}-delete`}
                          variant="danger"
                          size="sm"
                          type="button"
                          disabled={busy}
                          dataEffect={`remove:${pageId}`}
                          onClick={() => {
                            void doDeleteVersion(row);
                          }}
                        >
                          刪除
                        </Button>
                      </>
                    ) : null}
                  </span>
                );
              }
              if (columnKey === 'clone') {
                return (
                  <Button
                    uiId={`${pageId}-version-row-${id}-clone`}
                    variant="default"
                    size="sm"
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      void doCloneVersion(row);
                    }}
                  >
                    複製
                  </Button>
                );
              }
              if (columnKey === 'verify') {
                // 審核四態字色（:123-132）＋頁型鈕（送出審核 verify 空 :134；通過/未通過
                // verify=='NOT'＋verifyerId==loginAccountId→D23-9 種子旗標 :136-137）。
                return (
                  <span data-ui-id={`${pageId}-version-row-${id}-verify`}>
                    <span className={verifyToneOf(verify)}>
                      {verify === '' ? '未審核' : verify === 'NOT' ? '審核中' : verify === 'PASS' ? '通過' : '未通過'}
                    </span>
                    <br />
                    {verify === '' ? (
                      <Button
                        uiId={`${pageId}-version-row-${id}-send-verify`}
                        variant="default"
                        size="sm"
                        type="button"
                        disabled={busy}
                        dataEffect={`update:${pageId}`}
                        onClick={() => {
                          void sendVerify(row, 'NOT');
                        }}
                      >
                        送出審核
                      </Button>
                    ) : null}
                    {verify === 'NOT' && verifyerIsMe === '1' ? (
                      <>
                        <Button
                          uiId={`${pageId}-version-row-${id}-verify-pass`}
                          variant="primary"
                          size="sm"
                          type="button"
                          disabled={busy}
                          dataEffect={`update:${pageId}`}
                          onClick={() => {
                            void sendVerify(row, 'PASS');
                          }}
                        >
                          通過
                        </Button>{' '}
                        <Button
                          uiId={`${pageId}-version-row-${id}-verify-fail`}
                          variant="danger"
                          size="sm"
                          type="button"
                          disabled={busy}
                          dataEffect={`update:${pageId}`}
                          onClick={() => {
                            void sendVerify(row, 'FAIL');
                          }}
                        >
                          未通過
                        </Button>
                      </>
                    ) : null}
                  </span>
                );
              }
              return String(row[columnKey] ?? '');
            }}
          />
        ) : null}
      </ContentPanel>
      <ProcessingOverlay uiId={`${pageId}-processing`} open={busy} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 常用題型格式維護（form-template-field-type-list）
// ---------------------------------------------------------------------------

export function FieldTypeListBody({ pageId }: { pageId: string }): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  // 批次 52 修訂（b52-calibrate3，F-2）：OPTION_SET_OVERRIDES 命中＝凍結選項集取代寫死常數（規格 §7.1）。
  const overrideOptions = (name: string) => selectOptionsOverride(pageId, name);
  const [organizationId, setOrganizationId] = useState(ACCOUNT_ORGANIZATION_OPTIONS[0]!.value);
  const [jobTypeId, setJobTypeId] = useState(ACCOUNT_JOB_TYPE_OPTIONS[0]!.value);
  const [type, setType] = useState('');
  const [rows, setRows] = useState<readonly Row[]>(FIELD_TYPE_SEED);
  const [editOpen, setEditOpen] = useState(false);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [editId, setEditId] = useState('');
  const [nameError, setNameError] = useState('');
  const [busy, setBusy] = useState(false);

  async function doQuery(): Promise<void> {
    setBusy(true);
    try {
      // formTemplate/getFieldTypeListByTypeAjax（doQuery :101/:105；server 端過濾＝種子直出 D23-3 同款）。
      // 批次 43：purpose=query hidden（legacy form[1] 內常數參數）隨查詢送出（C5-h）。
      const result = await dataSource.list(pageId, {
        page: 1,
        pageSize: 50,
        filters: { organizationId, jobTypeId, type, purpose: 'query' },
      });
      setRows(result.rows.length > 0 ? result.rows : FIELD_TYPE_SEED);
    } catch (cause: unknown) {
      setBusy(false);
      await dialogs.alert(legacyAlertFallback(cause));
    } finally {
      setBusy(false);
    }
  }

  /** 名稱連結 → 題型編輯 modal（showUpdate :69-74；欄位簡化＝D23-14）。 */
  function openEdit(row: Row): void {
    setEditId(String(row['id'] ?? ''));
    setEditValues({
      name: String(row['name'] ?? ''),
      sampleTitle: String(row['sampleTitle'] ?? ''),
      subjectStyle: String(row['subjectStyle'] ?? ''),
      format: String(row['format'] ?? ''),
      grade: String(row['grade'] ?? ''),
    });
    setNameError('');
    setEditOpen(true);
  }

  async function saveFieldType(): Promise<void> {
    // 未輸入名稱!（js-my/fieldTypeList.js:83-87）。
    if ((editValues['name'] ?? '') === '') {
      setNameError('未輸入名稱!');
      return;
    }
    setBusy(true);
    try {
      // saveFieldTypeAjax（:145；CKEDITOR sampleTitle → 文字域鏡像；update 誠實標註）。
      await dataSource.update(pageId, editId, { ...editValues });
      setRows((current) => current.map((row) => (String(row['id'] ?? '') === editId ? { ...row, ...editValues } : row)));
      setEditOpen(false);
      setBusy(false); // 先收遮罩再跳資訊對話框（issue #56）
      await dialogs.info('存檔成功【雛形值待實機校準】');
    } catch (cause: unknown) {
      setBusy(false);
      await dialogs.alert(legacyAlertFallback(cause));
    } finally {
      setBusy(false);
    }
  }

  /** 複製（showClone :75-80；create 新列『(複製)』）。 */
  async function doClone(row: Row): Promise<void> {
    setBusy(true);
    try {
      await dataSource.create(pageId, { ...row, name: `${String(row['name'] ?? '')}(複製)` });
      setRows((current) => [...current, { ...row, id: `${String(row['id'] ?? '')}-clone`, name: `${String(row['name'] ?? '')}(複製)` }]);
      setBusy(false); // 先收遮罩再跳資訊對話框（issue #56）
      await dialogs.info('複製成功【雛形值待實機校準】');
    } catch (cause: unknown) {
      setBusy(false);
      await dialogs.alert(legacyAlertFallback(cause));
    } finally {
      setBusy(false);
    }
  }

  /** 刪除（showDelete :81-85；confirm＋remove）。 */
  async function doDelete(row: Row): Promise<void> {
    const confirmed = await dialogs.confirm('是否確定要刪除?');
    if (!confirmed) {
      return;
    }
    setBusy(true);
    try {
      await dataSource.remove(pageId, String(row['id'] ?? ''));
      setRows((current) => current.filter((candidate) => candidate !== row));
      setBusy(false); // 先收遮罩再跳資訊對話框（issue #56）
      await dialogs.info('刪除成功【雛形值待實機校準】');
    } catch (cause: unknown) {
      setBusy(false);
      await dialogs.alert(legacyAlertFallback(cause));
    } finally {
      setBusy(false);
    }
  }

  // 批次 43（#41）：表頭序列照 legacy-table-headers（fieldTypeList.jsp:235 表頭＝''/訓練類別/
  // 名稱(顯示於下拉選單)；原寫死「職類」＝文字不同 1 筆）。
  const columns: readonly TableColumn[] = [
    { key: 'actions', header: '', align: 'center' },
    { key: 'jobTypeName', header: '訓練類別', align: 'center' },
    { key: 'name', header: '名稱(顯示於下拉選單)', align: 'center' },
  ];

  return (
    <div data-ui-id={pageId}>
      {/* 題型編輯 modal（欄位簡化＝D23-14；sampleTitle CKEDITOR 以文字域鏡像）。 */}
      {editOpen ? (
        <ModalFrame
          uiId={`${pageId}-field-type-dialog`}
          open={true}
          title="常用題型格式維護"
          size="lg"
          onClose={() => setEditOpen(false)}
          footer={
            <>
              <Button uiId={`${pageId}-field-type-dialog-cancel`} variant="default" size="sm" type="button" disabled={busy} onClick={() => setEditOpen(false)}>
                取消
              </Button>
              <Button
                uiId={`${pageId}-field-type-dialog-save`}
                variant="primary"
                size="sm"
                type="button"
                disabled={busy}
                dataEffect={`update:${pageId}`}
                onClick={() => {
                  void saveFieldType();
                }}
              >
                存檔
              </Button>
            </>
          }
        >
          <div data-ui-id={`${pageId}-field-type-dialog-form`} className="space-y-3">
            {(
              [
                ['name', '名稱(顯示於下拉選單)：'],
                ['sampleTitle', '樣題（CKEDITOR）：'],
                ['subjectStyle', '主題樣式：'],
                ['format', '格式：'],
                ['grade', '計分：'],
              ] as const
            ).map(([key, label]) => (
              <div key={key} className="flex items-start gap-2">
                <label className="w-44 shrink-0 text-sm" htmlFor={`${pageId}-field-type-dialog-${toUiKey(key)}`}>
                  {label}
                </label>
                <div className="flex-1">
                  {key === 'sampleTitle' ? (
                    <textarea
                      data-ui-id={`${pageId}-field-type-dialog-sample-title`}
                      className="w-full rounded border border-border p-2 text-sm"
                      rows={3}
                      value={editValues[key] ?? ''}
                      onChange={(event) => setEditValues((current) => ({ ...current, [key]: event.target.value }))}
                    />
                  ) : (
                    <TextInput
                      uiId={`${pageId}-field-type-dialog-${toUiKey(key)}`}
                      name={key}
                      value={editValues[key] ?? ''}
                      onChange={(next) => setEditValues((current) => ({ ...current, [key]: next }))}
                    />
                  )}
                  {key === 'name' && nameError !== '' ? (
                    <div data-ui-id={`${pageId}-field-type-dialog-error-name`} className="text-danger text-xs">
                      {nameError}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </ModalFrame>
      ) : null}
      <ContentPanel uiId={`${pageId}-panel`} title="常用題型格式維護" headingLevel={panelHeadingLevelFor(pageId)}>
        {/* 查詢 3 select（fieldTypeForm :186-218：organizationId/jobTypeId/type 欄位類型）。
            批次 43（#41）：包 legacy form（form[1] action=/formTemplate/fieldType/fieldTypeList
            method=POST；R-diff form[1] 15 節點對位）。 */}
        <form
          data-ui-id={`${pageId}-query-form`}
          action="/formTemplate/fieldType/fieldTypeList"
          method="POST"
          onSubmit={(event) => event.preventDefault()}
        >
        {/* purpose hidden（legacy form[1] 內 input[name=purpose][value=query]；批次 43 對位）。 */}
        <input type="hidden" name="purpose" value="query" />
        <div data-ui-id={`${pageId}-query`} className="mb-4 grid grid-cols-2 gap-2">
          {(
            [
              // 批次 43：label 逐字（fieldTypeForm legacy 無冒號：醫院/訓練類別/欄位類型）。
              ['organizationId', '醫院', overrideOptions('organizationId') ?? ACCOUNT_ORGANIZATION_OPTIONS, organizationId, setOrganizationId],
              ['jobTypeId', '訓練類別', overrideOptions('jobTypeId') ?? ACCOUNT_JOB_TYPE_OPTIONS, jobTypeId, setJobTypeId],
              ['type', '欄位類型', overrideOptions('type') ?? FIELD_TYPE_SEED.map((row) => ({ value: String(row['name'] ?? ''), label: String(row['name'] ?? '') })), type, setType],
            ] as const
          ).map(([keyName, label, options, value, set]) => (
            <div key={keyName} className="flex items-center gap-2">
              <label className="w-28 shrink-0 text-sm" htmlFor={`${pageId}-query-${keyName}`}>
                {label}
              </label>
              <Select uiId={`${pageId}-query-${keyName}`} name={keyName} value={value} onChange={set} options={options} {...(overrideOptions(keyName) ? {} : { placeholder: '請選擇' })} />
            </div>
          ))}
          <div className="col-span-2">
            <Button uiId={`${pageId}-query-btn`} variant="primary" size="sm" type="button" disabled={busy} onClick={() => void doQuery()}>
              查詢
            </Button>
          </div>
        </div>
        </form>
        {/* 批次 43（#41）：右欄說明（fieldTypeList.jsp:226-231 說明:＋ol×1；legacy-aside-notes 地圖）。 */}
        <LegacyAsideBlocks pageId={pageId} />
        {/* 表 3 欄（:45-67）。 */}
        <Table
          uiId={`${pageId}-result-table`}
          columns={columns}
          rows={rows}
          rowKey={(row) => String(row['id'] ?? '').toLowerCase()}
          emptyText={t('table.empty')}
          renderCell={(row, columnKey) => {
            const id = String(row['id'] ?? '').toLowerCase();
            if (columnKey === 'actions') {
              return (
                <span>
                  <Button
                    uiId={`${pageId}-result-row-${id}-clone`}
                    variant="default"
                    size="sm"
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      void doClone(row);
                    }}
                  >
                    複製
                  </Button>
                  <br />
                  <Button
                    uiId={`${pageId}-result-row-${id}-delete`}
                    variant="danger"
                    size="sm"
                    type="button"
                    disabled={busy}
                    dataEffect={`remove:${pageId}`}
                    onClick={() => {
                      void doDelete(row);
                    }}
                  >
                    刪除
                  </Button>
                </span>
              );
            }
            if (columnKey === 'name') {
              return (
                <button
                  type="button"
                  className="cursor-pointer text-[#1ab394] underline"
                  data-ui-id={`${pageId}-result-row-${id}-update`}
                  onClick={() => openEdit(row)}
                >
                  {String(row['name'] ?? '')}
                </button>
              );
            }
            return String(row[columnKey] ?? '');
          }}
        />
      </ContentPanel>
      <ProcessingOverlay uiId={`${pageId}-processing`} open={busy} />
    </div>
  );
}
